import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert, Card, Center, Container, Badge, Group, Loader, SimpleGrid, Text, Title,
} from '@mantine/core'
import type { ReactNode } from 'react'
import {
  ArrowRightLeft, BarChart3, BookOpen, Brackets, Calculator, Circle, FileText,
  GitCompare, Lightbulb, PenTool, Percent, PieChart, Scale, TrendingUp, Triangle, Type,
} from 'lucide-react'
import { contentApi, type Course } from '../../api/content'

const domainColorMap: Record<string, string> = {
  'Information and Ideas': 'blue',
  'Craft and Structure': 'violet',
  'Expression of Ideas': 'teal',
  'Standard English Conventions': 'orange',
  Algebra: 'red',
  'Advanced Math': 'grape',
  'Problem-Solving and Data Analysis': 'cyan',
  'Geometry and Trigonometry': 'green',
}

const sectionColorMap: Record<string, string> = {
  'Reading & Writing': 'blue.4',
  'Reading and Writing': 'blue.4',
  Math: 'red.4',
}

function getCourseIcon(courseFile: string): ReactNode {
  const iconProps = { size: 20 }
  const iconMap: Record<string, ReactNode> = {
    central_ideas: <BookOpen {...iconProps} />,
    command_of_evidence_textual: <FileText {...iconProps} />,
    command_of_evidence_quantitative: <BarChart3 {...iconProps} />,
    inferences: <Lightbulb {...iconProps} />,
    words_in_context: <Type {...iconProps} />,
    text_structure: <PenTool {...iconProps} />,
    cross_text_connections: <GitCompare {...iconProps} />,
    rhetorical_synthesis: <PenTool {...iconProps} />,
    transitions: <ArrowRightLeft {...iconProps} />,
    boundaries: <Brackets {...iconProps} />,
    form_structure_and_sense: <PenTool {...iconProps} />,
    linear_equations_one_variable: <Calculator {...iconProps} />,
    linear_equations_two_variables: <TrendingUp {...iconProps} />,
    linear_inequalities: <Scale {...iconProps} />,
    equivalent_expressions: <Brackets {...iconProps} />,
    nonlinear_equations_one_variable: <Calculator {...iconProps} />,
    nonlinear_functions: <TrendingUp {...iconProps} />,
    systems_nonlinear_equations: <GitCompare {...iconProps} />,
    ratios_rates_proportional: <PieChart {...iconProps} />,
    percentages: <Percent {...iconProps} />,
    one_variable_data: <BarChart3 {...iconProps} />,
    two_variable_data: <TrendingUp {...iconProps} />,
    probability_conditional: <PieChart {...iconProps} />,
    inference_margin_of_error: <BarChart3 {...iconProps} />,
    evaluating_statistical_claims: <FileText {...iconProps} />,
    area_and_volume: <Triangle {...iconProps} />,
    lines_angles_triangles: <Triangle {...iconProps} />,
    right_triangle_trigonometry: <Triangle {...iconProps} />,
    circles: <Circle {...iconProps} />,
  }

  return iconMap[courseFile] ?? <BookOpen {...iconProps} />
}

export function CourseList() {
  const navigate = useNavigate()
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true

    const loadCourses = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await contentApi.getCourses()
        if (!isMounted) return
        setCourses(response.data)
      } catch {
        if (!isMounted) return
        setError('Unable to load courses right now.')
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    void loadCourses()

    return () => {
      isMounted = false
    }
  }, [])

  const coursesBySection = useMemo(() => {
    return courses.reduce<Record<string, Course[]>>((groups, course) => {
      if (!groups[course.portion_name]) {
        groups[course.portion_name] = []
      }
      groups[course.portion_name].push(course)
      return groups
    }, {})
  }, [courses])

  if (loading) {
    return <Center h={400}><Loader size="lg" /></Center>
  }

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Unable to load courses">
          {error}
        </Alert>
      </Container>
    )
  }

  return (
    <Container size="lg" py="xl">
      <Title order={2} mb="lg">SAT Mastery Courses</Title>

      {Object.entries(coursesBySection).map(([sectionName, sectionCourses]) => (
        <div key={sectionName}>
          <Title order={3} mb="sm" c={sectionColorMap[sectionName] ?? 'dark'}>
            {sectionName}
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} mb="xl">
            {sectionCourses.map(course => (
              <Card
                key={course.course_file}
                padding="md"
                radius="md"
                withBorder
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/courses/${course.course_file}`)}
              >
                <Group mb="xs">
                  {getCourseIcon(course.course_file)}
                  <Text fw={600} size="sm" style={{ flex: 1 }}>
                    {course.name}
                  </Text>
                </Group>
                <Badge
                  color={domainColorMap[course.domain_name] ?? 'gray'}
                  variant="light"
                  size="sm"
                >
                  {course.domain_name}
                </Badge>
              </Card>
            ))}
          </SimpleGrid>
        </div>
      ))}
    </Container>
  )
}
